import React, { useEffect, useRef, useState } from "react";
import { Modal, Spin } from "antd";
import { useSelector } from "react-redux";

import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import { uploadFileToCloudinary } from "../../helpers/cloudinary.helpers";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import api from "../../../../services/api";
import { notify } from "../../../../services/toast.config";

import type {
  Attachment,
  AttachmentEntityType,
} from "../../../../core/data/redux/projects/types";

interface AttachmentsPanelProps {
  entityType: AttachmentEntityType;
  entityId: number;
  folder?: string;
  currentUserId: number | null;
}

const formatBytes = (bytes: number | null): string => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const AttachmentsPanel: React.FC<AttachmentsPanelProps> = ({
  entityType,
  entityId,
  folder = "cms/projects/issues",
  currentUserId,
}) => {
  const activeCompany = useSelector(selectActiveCompany);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    if (!activeCompany.company_id) return;
    setLoading(true);
    try {
      const data = await issuesApi.listAttachments(
        activeCompany.company_id,
        activeCompany.company_type,
        entityType,
        entityId
      );
      setAttachments(
        Array.isArray(data?.attachments) ? data.attachments : []
      );
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to load attachments"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, activeCompany.company_id]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const cloud = await uploadFileToCloudinary(file, folder);
        if (!cloud) {
          notify.error(`Failed to upload ${file.name}`);
          continue;
        }
        await issuesApi.attachFile(
          activeCompany.company_id,
          activeCompany.company_type,
          {
            entity_type: entityType,
            entity_id: entityId,
            file_name: file.name,
            url: cloud.secure_url,
            public_id: cloud.public_id,
            resource_type: cloud.resource_type,
            mime_type: file.type || null,
            size_bytes: cloud.bytes ?? file.size,
          }
        );
      }
      await reload();
      notify.success("Files attached");
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to attach file"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = (a: Attachment) => {
    Modal.confirm({
      title: `Remove ${a.file_name}?`,
      okText: "Remove",
      okButtonProps: { danger: true },
      ...CONFIRM_MODAL_PROPS,
      onOk: async () => {
        try {
          await issuesApi.deleteAttachment(
            activeCompany.company_id,
            activeCompany.company_type,
            a.id
          );
          if (a.public_id) {
            try {
              await api.post("ers/cloudinary/deleteFile", {
                signature: localStorage.getItem("signature"),
                public_id: a.public_id,
                resource_type: a.resource_type || "auto",
              });
            } catch (cloudErr) {
              console.error("Cloudinary delete failed:", cloudErr);
            }
          }
          await reload();
          notify.success("Attachment removed");
        } catch (e: any) {
          notify.error(errMsg(e, "Failed to remove attachment"));
        }
      },
    });
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="text-muted small">
          {attachments.length} attachment{attachments.length === 1 ? "" : "s"}
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <button
            type="button"
            className="btn btn-sm btn-light"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Uploading…" : "Attach files"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-2">
          <Spin size="small" />
        </div>
      ) : attachments.length === 0 ? (
        <p className="text-muted small mb-0">No attachments yet.</p>
      ) : (
        <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
          {attachments.map((a) => {
            const canDelete =
              currentUserId == null ||
              a.uploaded_by == null ||
              a.uploaded_by === currentUserId;
            return (
              <li
                key={a.id}
                className="d-flex align-items-center gap-2 border rounded p-2"
              >
                <i className="ti ti-paperclip" />
                <div className="flex-grow-1 min-width-0">
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-truncate d-block"
                  >
                    {a.file_name}
                  </a>
                  <div className="text-muted small">
                    {formatBytes(a.size_bytes)}{" "}
                    {a.created_at && (
                      <span>· {humanReadableDate(a.created_at)}</span>
                    )}
                  </div>
                </div>
                {canDelete && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light text-danger"
                    onClick={() => handleDelete(a)}
                  >
                    <i className="ti ti-trash" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AttachmentsPanel;
